import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLanguage } from '../Layout/LanguageContext';

gsap.registerPlugin(ScrollTrigger);

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 0 8rem;
  background: linear-gradient(
    135deg,
    rgba(0, 51, 102, 0.3) 0%,
    rgba(94, 106, 117, 0.2) 50%,
    rgba(248, 249, 250, 0.4) 100%
  ),
  url('/images/TITELBILDREINKE.jpeg') center/cover no-repeat;
  box-shadow: none !important;
  border: none !important;

  @media (min-width: 1920px) {
    min-height: 100vh;
    padding: 0 10rem;
  }

  @media (min-width: 1600px) {
    padding: 0 8rem;
  }

  @media (max-width: 1200px) {
    padding: 0 4rem;
  }

  @media (max-width: 968px) {
    min-height: auto;
    height: auto;
    padding: 2rem 1rem 0.5rem 1rem;
    overflow: visible;
    align-items: flex-start;
    padding-top: 3rem;
  }

  @media (max-width: 768px) {
    padding: 2rem 1rem 0.5rem 1rem;
    padding-top: 2.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem 0.5rem 1rem;
    padding-top: 2rem;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  width: 100%;
  height: auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  align-items: center;
  align-content: center;
  z-index: 2;
  position: relative;
  padding: 0;
  text-align: center;

  @media (min-width: 1920px) {
    max-width: 1600px;
    gap: 5rem;
  }

  @media (min-width: 1400px) {
    max-width: 1400px;
    gap: 4rem;
  }

  @media (max-width: 1200px) {
    gap: 2rem;
  }

  @media (max-width: 968px) {
    display: flex;
    flex-direction: column;
    text-align: center;
    gap: 2rem;
    height: auto;
    align-items: center;
    max-width: 95%;
    margin: 0 auto;
    padding: 1rem 0 0 0;
    position: static;
    z-index: auto;
  }

  @media (max-width: 768px) {
    gap: 1.5rem;
    max-width: 100%;
    padding: 0.5rem 0 0 0;
  }
`;

const HeroText = styled.div`
  max-width: 800px;
  margin: 0 auto;
  color: var(--background-light);
  z-index: 3;
`;

const MainTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.1;
  background: linear-gradient(135deg, var(--background-light), var(--accent-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.25rem;
  font-weight: 400;
  margin-bottom: 2.5rem;
  line-height: 1.6;
  color: var(--background-light);
  opacity: 0.95;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, var(--accent-color), var(--accent-light));
  color: var(--primary-dark);
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(255, 180, 0, 0.3);
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(255, 180, 0, 0.4);
  }

  &:active {
    transform: translateY(-1px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1.75rem;
    font-size: 1rem;
  }
`;

const BackgroundOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    linear-gradient(135deg, rgba(0, 51, 102, 0.7) 0%, rgba(94, 106, 117, 0.6) 100%),
    url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
  z-index: 1;
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  pointer-events: none;
`;

const FloatingElement = styled(motion.div)`
  position: absolute;
  width: 4px;
  height: 4px;
  background: var(--accent-color);
  border-radius: 50%;
  opacity: 0.6;

  &:nth-child(1) { top: 20%; left: 10%; }
  &:nth-child(2) { top: 40%; right: 15%; }
  &:nth-child(3) { top: 60%; left: 20%; }
  &:nth-child(4) { top: 80%; right: 25%; }
  &:nth-child(5) { top: 30%; left: 80%; }
  &:nth-child(6) { top: 70%; left: 70%; }
`;

const heroContent = {
  de: {
    title: 'Klarheit für Ihre Daten. Weitblick für Ihr Unternehmen.',
    subtitle: 'Als südlichste IT-Beratung Deutschlands mit Sitz in Sonthofen unterstütze ich den Mittelstand, die Industrie und den Tourismus dabei, durch Daten, Automation und KI nachhaltige Wettbewerbsvorteile zu erzielen.',
    cta: 'Mehr erfahren',
  },
  en: {
    title: 'Clarity for Your Data. Vision for Your Business.',
    subtitle: 'As the southernmost IT consulting firm in Germany, based in Sonthofen, I support medium-sized businesses, industry, and tourism in achieving sustainable competitive advantages through data, automation, and AI.',
    cta: 'Learn More',
  },
};

const Hero: React.FC = () => {
  const { language } = useLanguage();
  const t = heroContent[language];
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (sectionRef.current) {
      gsap.fromTo(
        sectionRef.current,
        { opacity: 0 },
        { 
          opacity: 1, 
          duration: 1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top center',
            end: 'bottom center',
            toggleActions: 'play none none reverse'
          }
        }
      );
    }
  }, []);

  const scrollToServices = () => {
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HeroSection ref={sectionRef}>
      <BackgroundOverlay />
      <FloatingElements>
        <FloatingElement
          animate={{
            y: [0, -20, 0],
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <FloatingElement
          animate={{
            y: [0, 15, 0],
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.5,
          }}
        />
        <FloatingElement
          animate={{
            y: [0, -10, 0],
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
        />
        <FloatingElement
          animate={{
            y: [0, 25, 0],
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 4.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.5,
          }}
        />
        <FloatingElement
          animate={{
            y: [0, -15, 0],
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 3.2,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
        />
        <FloatingElement
          animate={{
            y: [0, 20, 0],
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 3.8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2.5,
          }}
        />
      </FloatingElements>
      
      <HeroContent>
        <HeroText>
          <MainTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t.title}
          </MainTitle>
          
          <Subtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {t.subtitle}
          </Subtitle>
          
          <CTAButton
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            onClick={scrollToServices}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t.cta}
          </CTAButton>
        </HeroText>
      </HeroContent>
    </HeroSection>
  );
};

export default Hero; 